import { Add, CloudUpload, Delete } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import Cookies from "js-cookie";
import { useEffect, useRef, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import useNotification from "../../hooks/useNotification";
import purchaseService from "../../services/purchase";
import queryDefault from "../../utils/queryDefault";

interface PurchaseItem {
  id?: number;
  qty: number;
  description: string;
  price: number;
  kategori: string;
}

interface PurchaseForm {
  usage_date: Date | null;
  request_date: Date | null;
  input_link: string;
  items: PurchaseItem[];
  status: string;
  nama_bank: string;
  no_rek: string;
  detail_toko: string;
  alamat_pengiriman: string;
  purchase_by: string;
  nama_supplier: string;
  jabatan_supplier: string;
  perusahaan_supplier: string;
  nama_penerima: string;
  jabatan_penerima: string;
  perusahaan_penerima: string;
}

interface Props {
  open: boolean;
  id: number | null;
  onClose: () => void;
}

export default function PurchaseEditModal({ open, id, onClose }: Props) {
  const notification = useNotification();
  const user = JSON.parse(Cookies.get("USER") || "{}");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadType, setUploadType] = useState<string>("payment proof");

  const form = useForm<PurchaseForm>({
    defaultValues: {
      usage_date: null,
      request_date: null,
      input_link: "",
      items: [{ qty: 1, description: "", price: 0, kategori: "" }],
      status: "",
      nama_bank: "",
      no_rek: "",
      detail_toko: "",
      alamat_pengiriman: "",
      purchase_by: "",
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "items",
  });

  const { data: purchaseData, isLoading } = useQuery(
    ["purchaseDetail", id],
    () => purchaseService.viewPurchaseDetail(String(id)),
    {
      ...queryDefault,
      enabled: !!id && open,
    }
  );

  useEffect(() => {
    if (purchaseData?.data) {
      const purchase = purchaseData.data;
      form.reset({
        usage_date: purchase.usage_date ? new Date(purchase.usage_date) : null,
        request_date: purchase.request_date ? new Date(purchase.request_date) : null,
        input_link: purchase.input_link || "",
        items: (purchase.items || [{ qty: 1, description: "", price: 0, kategori: "" }]).map((item: any) => ({
          ...item,
          kategori: item.kategori || "",
        })),
        status: purchase.status || "",
        nama_bank: purchase.nama_bank || "",
        no_rek: purchase.no_rek || "",
        detail_toko: purchase.detail_toko || "",
        alamat_pengiriman: purchase.alamat_pengiriman || "",
        purchase_by: purchase.purchase_by || "",
      });
    }
  }, [purchaseData, form]);

  const updatePurchase = useMutation(
    (params: any) => purchaseService.updatePurchase({ ...params, id: String(id) }),
    {
      onSuccess: () => {
        notification.onOpen({
          message: "Purchase updated successfully!",
          type: "success",
          position: "top",
        });
        onClose();
      },
      onError: () => {
        notification.onOpen({
          message: "Failed to update purchase",
          type: "error",
          position: "top",
        });
        setIsSubmitting(false);
      },
    }
  );

  const uploadFile = useMutation(
    ({ file, type }: { file: File; type: string }) =>
      purchaseService.uploadPurchaseFile(Number(id), file, type),
    {
      onSuccess: () => {
        notification.onOpen({
          message: "File uploaded successfully!",
          type: "success",
          position: "top",
        });
      },
      onError: () => {
        notification.onOpen({
          message: "Failed to upload file",
          type: "error",
          position: "top",
        });
      },
    }
  );

  const onSubmit = (data: PurchaseForm) => {
    setIsSubmitting(true);
    const payload = {
      usage_date: data.usage_date?.toISOString().split("T")[0],
      request_date: data.request_date?.toISOString().split("T")[0],
      input_link: data.input_link,
      items: data.items,
      last_updated_by: user.id,
      status: data.status,
      nama_bank: data.nama_bank,
      no_rek: data.no_rek,
      detail_toko: data.detail_toko,
      alamat_pengiriman: data.alamat_pengiriman,
      purchase_by: data.purchase_by,
      nama_supplier: data.nama_supplier,
      jabatan_supplier: data.jabatan_supplier,
      perusahaan_supplier: data.perusahaan_supplier,
      nama_penerima: data.nama_penerima,
      jabatan_penerima: data.jabatan_penerima,
      perusahaan_penerima: data.perusahaan_penerima,
    };
    updatePurchase.mutate(payload);
  };

  const addItem = () => {
    append({ qty: 1, description: "", price: 0, kategori: "" });
  };

  const removeItem = (index: number) => {
    if (fields.length > 1) {
      remove(index);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && id) {
      uploadFile.mutate({ file, type: uploadType });
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Edit Purchase</DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <Typography>Loading...</Typography>
        ) : (
          <Box component="form" onSubmit={form.handleSubmit(onSubmit)} sx={{ mt: 1 }}>
            <Stack spacing={3}>
              <Controller
                name="usage_date"
                control={form.control}
                render={({ field }) => (
                  <DatePicker
                    label="Usage Date"
                    value={field.value}
                    onChange={field.onChange}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                )}
              />

              <Controller
                name="request_date"
                control={form.control}
                render={({ field }) => (
                  <DatePicker
                    label="Request Date"
                    value={field.value}
                    onChange={field.onChange}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                )}
              />

              <Controller
                name="input_link"
                control={form.control}
                render={({ field }) => (
                  <TextField {...field} label="Input Link" fullWidth variant="outlined" />
                )}
              />

              <Stack direction={{ xs: "column", sm: "row" }} spacing={3}>
                <Controller
                  name="nama_bank"
                  control={form.control}
                  render={({ field }) => (
                    <TextField {...field} label="Bank Name" fullWidth variant="outlined" />
                  )}
                />
                <Controller
                  name="no_rek"
                  control={form.control}
                  render={({ field }) => (
                    <TextField {...field} label="Account Number" fullWidth variant="outlined" />
                  )}
                />
              </Stack>

              <Controller
                name="detail_toko"
                control={form.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Store Detail"
                    fullWidth
                    variant="outlined"
                    multiline
                    rows={2}
                  />
                )}
              />

              <Controller
                name="alamat_pengiriman"
                control={form.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Alamat Pengiriman"
                    fullWidth
                    variant="outlined"
                    multiline
                    rows={2}
                  />
                )}
              />

              <Controller
                name="status"
                control={form.control}
                render={({ field: { ref, ...field }, fieldState }) => (
                  <FormControl fullWidth size="small" error={Boolean(fieldState.error?.message)}>
                    <InputLabel>Status</InputLabel>
                    <Select
                      {...field}
                      label="Status"
                    >
                      <MenuItem value="draft">Draft</MenuItem>
                      <MenuItem value="processed">Processed</MenuItem>
                      <MenuItem value="declined">Declined</MenuItem>
                      <MenuItem value="done">Done</MenuItem>
                    </Select>
                    <FormHelperText>{fieldState.error?.message}</FormHelperText>
                  </FormControl>
                )}
              />

              <Controller
                name="purchase_by"
                control={form.control}
                render={({ field: { ref, ...field }, fieldState }) => (
                  <FormControl fullWidth size="small" error={Boolean(fieldState.error?.message)}>
                    <InputLabel>Purchase By</InputLabel>
                    <Select
                      {...field}
                      label="Purchase By"
                    >
                      <MenuItem value="CC">CC</MenuItem>
                      <MenuItem value="Finance">Finance</MenuItem>
                    </Select>
                    <FormHelperText>{fieldState.error?.message}</FormHelperText>
                  </FormControl>
                )}
              />

              <Controller
                name="nama_supplier"
                control={form.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Nama Supplier"
                    fullWidth
                    variant="outlined"
                  />
                )}
              />

              <Controller
                name="jabatan_supplier"
                control={form.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Jabatan Supplier"
                    fullWidth
                    variant="outlined"
                  />
                )}
              />

              <Controller
                name="perusahaan_supplier"
                control={form.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Perusahaan Supplier"
                    fullWidth
                    variant="outlined"
                  />
                )}
              />

              <Controller
                name="nama_penerima"
                control={form.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Nama Penerima"
                    fullWidth
                    variant="outlined"
                  />
                )}
              />

              <Controller
                name="jabatan_penerima"
                control={form.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Jabatan Penerima"
                    fullWidth
                    variant="outlined"
                  />
                )}
              />

              <Controller
                name="perusahaan_penerima"
                control={form.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Perusahaan Penerima"
                    fullWidth
                    variant="outlined"
                  />
                )}
              />

              <Box>
                <Typography variant="h6" gutterBottom>
                  Purchase Items
                </Typography>
                <Stack spacing={2}>
                  {fields.map((field, index) => (
                    <Stack key={field.id} direction="row" spacing={2} alignItems="center">
                      <Controller
                        name={`items.${index}.qty`}
                        control={form.control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            type="number"
                            label="Qty"
                            size="small"
                            onChange={(e) => field.onChange(Number(e.target.value))}
                          />
                        )}
                      />
                      <Controller
                        name={`items.${index}.description`}
                        control={form.control}
                        render={({ field }) => (
                          <TextField {...field} label="Description" size="small" fullWidth />
                        )}
                      />
                      <Controller
                        name={`items.${index}.price`}
                        control={form.control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            type="number"
                            label="Price"
                            size="small"
                            onChange={(e) => field.onChange(Number(e.target.value))}
                          />
                        )}
                      />
                      <Controller
                        name={`items.${index}.kategori`}
                        control={form.control}
                        render={({ field: { ref, ...field }, fieldState }) => (
                          <FormControl size="small" error={Boolean(fieldState.error?.message)} sx={{ minWidth: 180 }}>
                            <InputLabel>Kategori</InputLabel>
                            <Select
                              {...field}
                              label="Kategori"
                            >
                              <MenuItem value="Perlengkapan">Perlengkapan</MenuItem>
                              <MenuItem value="Aset">Aset</MenuItem>
                              <MenuItem value="Bahan Baku">Bahan Baku</MenuItem>
                              <MenuItem value="Pengiriman">Pengiriman</MenuItem>
                            </Select>
                            <FormHelperText>{fieldState.error?.message}</FormHelperText>
                          </FormControl>
                        )}
                      />
                      <IconButton
                        onClick={() => removeItem(index)}
                        disabled={fields.length === 1}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </Stack>
                  ))}
                  <Button
                    variant="outlined"
                    startIcon={<Add />}
                    onClick={addItem}
                    sx={{ alignSelf: "flex-start" }}
                  >
                    Add Item
                  </Button>
                </Stack>
              </Box>

              <Stack direction="row" spacing={2} flexWrap="wrap">
                <Button type="submit" variant="contained" disabled={isSubmitting}>
                  {isSubmitting ? "Updating..." : "Update Purchase"}
                </Button>
                {user.role_id === 1 && (
                  <>
                    <Button
                      variant="outlined"
                      startIcon={<CloudUpload />}
                      onClick={() => {
                        setUploadType("payment proof");
                        fileInputRef.current?.click();
                      }}
                      disabled={uploadFile.isLoading}
                    >
                      {uploadFile.isLoading ? "Uploading..." : "Upload Payment Proof"}
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<CloudUpload />}
                      onClick={() => {
                        setUploadType("npwp");
                        fileInputRef.current?.click();
                      }}
                      disabled={uploadFile.isLoading}
                    >
                      {uploadFile.isLoading ? "Uploading..." : "Upload NPWP"}
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<CloudUpload />}
                      onClick={() => {
                        setUploadType("ktp");
                        fileInputRef.current?.click();
                      }}
                      disabled={uploadFile.isLoading}
                    >
                      {uploadFile.isLoading ? "Uploading..." : "Upload KTP"}
                    </Button>
                  </>
                )}
                <Button variant="outlined" onClick={onClose}>
                  Cancel
                </Button>
              </Stack>
            </Stack>
          </Box>
        )}
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleFileUpload}
          accept=".pdf,.doc,.docx,.xls,.xlsx"
        />
      </DialogContent>
    </Dialog>
  );
}
